import { supabase } from '../lib/supabase';

// Generate a random challenge for WebAuthn
const generateChallenge = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return array;
};

// Convert ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes?.byteLength; i++) {
    binary += String.fromCharCode(bytes?.[i]);
  }
  return btoa(binary);
};

// Convert Base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  let binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary?.length; i++) {
    bytes[i] = binary?.charCodeAt(i);
  }
  return bytes?.buffer;
};

export const webAuthnService = {
  // Check if WebAuthn is supported
  isSupported() {
    try {
      return !!(
        window.PublicKeyCredential &&
        navigator.credentials &&
        typeof navigator.credentials?.create === 'function' &&
        typeof navigator.credentials?.get === 'function'
      );
    } catch (error) {
      console.warn('WebAuthn support check failed:', error);
      return false;
    }
  },

  // Register a new WebAuthn credential for a user
  async registerCredential(userId, email, refreshToken) {
    try {
      if (!this.isSupported()) {
        throw new Error('WebAuthn is not supported on this device');
      }

      const challenge = generateChallenge();

      const publicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: 'HR Portal Pro',
          id: window.location?.hostname,
        },
        user: {
          id: new TextEncoder()?.encode(userId),
          name: email,
          displayName: email,
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          requireResidentKey: false,
        },
        timeout: 60000,
        attestation: 'none',
      };

      const credential = await navigator.credentials?.create({
        publicKey: publicKeyCredentialCreationOptions,
      });

      if (!credential) {
        throw new Error('Failed to create credential');
      }

      // Store credential with refresh token for session restoration
      const credentialData = {
        id: credential?.id,
        rawId: arrayBufferToBase64(credential?.rawId),
        type: credential?.type,
        userId,
        email,
        refreshToken, // Store refresh token for session restoration
        createdAt: new Date()?.toISOString(),
      };

      // Get existing credentials
      const existingCredentials = JSON.parse(
        localStorage.getItem('webauthn_credentials') || '[]'
      );

      // Remove any existing credentials for this email
      const filteredCredentials = existingCredentials?.filter(
        (cred) => cred?.email !== email
      );

      // Add new credential
      filteredCredentials?.push(credentialData);
      localStorage.setItem(
        'webauthn_credentials',
        JSON.stringify(filteredCredentials)
      );

      return { success: true, credentialId: credential?.id };
    } catch (error) {
      console.error('WebAuthn registration failed:', error);
      return {
        success: false,
        error: error?.message || 'Failed to register biometric authentication',
      };
    }
  },

  // Authenticate using WebAuthn and restore Supabase session
  async authenticate() {
    try {
      if (!this.isSupported()) {
        throw new Error('WebAuthn is not supported on this device');
      }

      // Get stored credentials
      const storedCredentials = JSON.parse(
        localStorage.getItem('webauthn_credentials') || '[]'
      );

      if (storedCredentials?.length === 0) {
        throw new Error('No biometric credentials found. Please register first.');
      }

      const challenge = generateChallenge();

      // Get all credential IDs
      const allowCredentials = storedCredentials?.map((cred) => ({
        id: base64ToArrayBuffer(cred?.rawId),
        type: 'public-key',
        transports: ['internal'],
      }));

      const publicKeyCredentialRequestOptions = {
        challenge,
        allowCredentials,
        timeout: 60000,
        userVerification: 'required',
      };

      const assertion = await navigator.credentials?.get({
        publicKey: publicKeyCredentialRequestOptions,
      });

      if (!assertion) {
        throw new Error('Authentication failed');
      }

      // Find the matching credential
      const matchedCredential = storedCredentials?.find(
        (cred) => cred?.id === assertion?.id
      );

      if (!matchedCredential) {
        throw new Error('Credential not found');
      }

      // Restore Supabase session using stored refresh token
      const { data, error } = await supabase?.auth?.setSession({
        access_token: matchedCredential?.refreshToken,
        refresh_token: matchedCredential?.refreshToken,
      });

      if (error) {
        // If session restoration fails, try to refresh
        const { data: refreshData, error: refreshError } = await supabase?.auth?.refreshSession({
          refresh_token: matchedCredential?.refreshToken,
        });

        if (refreshError) {
          throw new Error('Session expired. Please sign in again and re-register Face ID.');
        }

        return {
          success: true,
          userId: matchedCredential?.userId,
          email: matchedCredential?.email,
          session: refreshData?.session,
        };
      }

      return {
        success: true,
        userId: matchedCredential?.userId,
        email: matchedCredential?.email,
        session: data?.session,
      };
    } catch (error) {
      console.error('WebAuthn authentication failed:', error);
      return {
        success: false,
        error: error?.message || 'Biometric authentication failed',
      };
    }
  },

  // Check if user has registered credentials (supports both email and username)
  async hasCredentials(identifier = null) {
    try {
      const storedCredentials = JSON.parse(
        localStorage.getItem('webauthn_credentials') || '[]'
      );

      if (identifier) {
        // Check if identifier is email format
        const emailRegex = /\S+@\S+\.\S+/;
        const isEmail = emailRegex?.test(identifier);
        let checkEmail = identifier;

        // If username, lookup email
        if (!isEmail) {
          const { data: emailData, error: lookupError } = await supabase?.rpc('get_email_from_username', { p_username: identifier });
          
          if (lookupError || !emailData) {
            return false;
          }
          
          checkEmail = emailData;
        }

        return storedCredentials?.some((cred) => cred?.email === checkEmail);
      }

      return storedCredentials?.length > 0;
    } catch (error) {
      console.error('Error checking credentials:', error);
      return false;
    }
  },

  // Remove credentials for a user (supports both email and username)
  async removeCredentials(identifier) {
    try {
      // Check if identifier is email format
      const emailRegex = /\S+@\S+\.\S+/;
      const isEmail = emailRegex?.test(identifier);
      let removeEmail = identifier;

      // If username, lookup email
      if (!isEmail) {
        const { data: emailData, error: lookupError } = await supabase?.rpc('get_email_from_username', { p_username: identifier });
        
        if (lookupError || !emailData) {
          return { success: false, error: 'User not found' };
        }
        
        removeEmail = emailData;
      }

      const storedCredentials = JSON.parse(
        localStorage.getItem('webauthn_credentials') || '[]'
      );

      const filteredCredentials = storedCredentials?.filter(
        (cred) => cred?.email !== removeEmail
      );

      localStorage.setItem(
        'webauthn_credentials',
        JSON.stringify(filteredCredentials)
      );

      return { success: true };
    } catch (error) {
      console.error('Error removing credentials:', error);
      return { success: false, error: error?.message };
    }
  },
};

export default webAuthnService;
